import React, { PropsWithChildren, useContext, useMemo } from "react";
import { PublicConfigurationContext, UserInfoContext } from "../Context";
import { getAIConfig } from "@aidkitorg/types/lib/config";
import { ChatConversationProvider } from "../Contexts/ChatContext";
import { AudioInterfaceProvider } from "../Contexts/AudioInterfaceContext";
import { DefaultChatSessionProvider } from "../Contexts/ChatSessionContext";
import { ApplicantContext } from "../Contexts/ApplicantContext";
import { evalConditional } from "@aidkitorg/roboscreener/lib/compute/util";
import { CompileExpressionToJS } from "@aidkitorg/types/lib/translation/expr_to_js";
import { MessageTemplateRef } from "@aidkitorg/types/lib/survey";
import { conversationApplications } from "@aidkitorg/aidkit/lib/convoai/schemas/ConversationItem";
import type { CoachGenerationTrace } from "@aidkitorg/types/lib/convoai";

type ConversationApplicationType = typeof conversationApplications[number];
export type AICoachState = {
  enabled: boolean,
  template?: MessageTemplateRef,
  config?: ReturnType<typeof getAIConfig>,
  aiConfigKey?: 'aiCoach' | 'appReviewAssistant',
  application: ConversationApplicationType,
  applicantUid?: string,
  includeTrace?: boolean,
  onTrace?: (trace: CoachGenerationTrace | null) => void,
  surfaceErrors?: boolean,
  userName: string
};

const defaults: AICoachState = {
  enabled: false,
  userName: 'U',
  application: 'coach'
}

export const AICoachContext = React.createContext<AICoachState>(defaults);

export function AICoachProvider({
  children,
  template,
  sessionName,
  application = 'coach',
  applicantUid,
  includeTrace,
  onTrace,
  surfaceErrors
}: PropsWithChildren & {
  template?: MessageTemplateRef,
  sessionName?: string,
  application?: typeof conversationApplications[number],
  applicantUid?: string,
  includeTrace?: boolean,
  onTrace?: (trace: CoachGenerationTrace | null) => void,
  surfaceErrors?: boolean
}): JSX.Element {

  const aiConfigKey = application === "reviewCoach" ? "appReviewAssistant" : "aiCoach";
  const config = useContext(PublicConfigurationContext);
  const { info } = useContext(ApplicantContext);
  const user = useContext(UserInfoContext);
  const enabled = useMemo(() => {
    if (application === 'playground') {
      return true;
    }

    const aiConfig = config.capabilities?.[aiConfigKey];

    if (!aiConfig || aiConfig.disabled) {
      return false;
    }

    if(user.tags?.includes('ai_coach')) {
      return true;
    }

    // Check subsurvey restriction
    const limitToSubsurveys = aiConfig.limitToSubsurveys ?? [];
    const currentSubsurvey = window.location.pathname.replace(/^\/(p\/)*/ig, '').split('/')[0].toLowerCase();
    if (limitToSubsurveys.length && !limitToSubsurveys.includes(currentSubsurvey)) {
      return false;
    }

    // Check allowedApplicants expression
    if (aiConfig.allowedApplicants) {
      const expression = CompileExpressionToJS(aiConfig.allowedApplicants);
      const allowed = evalConditional(expression, info, {}, {});
      return !!allowed;
    }

    return true;
  }, [application, aiConfigKey, config.capabilities?.[aiConfigKey], info, user]);

  const userName = aiConfigKey === 'aiCoach' ? info.legal_name : user?.name;

  const stateValue: AICoachState = {
    config: config.capabilities?.[aiConfigKey],
    template,
    enabled,
    aiConfigKey,
    application,
    applicantUid,
    includeTrace,
    onTrace,
    surfaceErrors,
    userName: userName?.substring(0, 1)?.toUpperCase() || defaults.userName
  };

  if (!enabled) {
    return <>{children}</>;
  }

  return <AudioInterfaceProvider>
    <ChatConversationProvider>
      <DefaultChatSessionProvider conversationIdKey={sessionName}>
        <AICoachContext.Provider value={stateValue}>
          {children}
        </AICoachContext.Provider>
      </DefaultChatSessionProvider>
    </ChatConversationProvider>
  </AudioInterfaceProvider>;
}

// sugar
export function useAICoach(): AICoachState {
  const AICoach = useContext(AICoachContext);
  return AICoach;
}
